import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import StickyNote2OutlinedIcon from '@mui/icons-material/StickyNote2Outlined';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import DrawOutlinedIcon from '@mui/icons-material/DrawOutlined';
import LoginOutlinedIcon from '@mui/icons-material/LoginOutlined';
import TimelineIcon from '@mui/icons-material/Timeline';
import { formatDistanceToNow } from 'date-fns';
import { enhancedApi } from '../api';

const FILTER_OPTIONS = [
  { value: 'all', label: 'All Activity', types: 'all' },
  { value: 'tasks', label: 'Tasks', types: 'workflow' },
  { value: 'messages', label: 'Messages', types: 'email,communication' },
  { value: 'files', label: 'Files', types: 'file_upload,document_request' },
  { value: 'invoices', label: 'Invoices', types: 'invoice' },
  { value: 'notes', label: 'Notes', types: 'note' },
];

const KIND = {
  TASK_COMPLETED: 'task_completed',
  MESSAGE_SENT: 'message_sent',
  FILE_UPLOADED: 'file_uploaded',
  INVOICE_SENT: 'invoice_sent',
  NOTE_ADDED: 'note_added',
  WORKFLOW_ASSIGNED: 'workflow_assigned',
  CLIENT_SIGNED: 'client_signed',
  PORTAL_LOGIN: 'portal_login',
  ESIGNATURE_PENDING: 'esignature_pending',
};

function formatRelativeTime(ts) {
  if (!ts) return '';
  try {
    return formatDistanceToNow(new Date(ts), { addSuffix: true });
  } catch {
    return '';
  }
}

function classifyItem(item) {
  const t = item.type;
  if (t === 'note') return KIND.NOTE_ADDED;
  if (t === 'invoice') return KIND.INVOICE_SENT;
  if (t === 'file_upload' || t === 'document_request') return KIND.FILE_UPLOADED;
  if (t === 'email' || t === 'communication') return KIND.MESSAGE_SENT;
  if (t === 'workflow') return KIND.WORKFLOW_ASSIGNED;
  if (t === 'esignature') {
    const s = String(item.summary || '').toLowerCase();
    if (s.includes('signed') || s.includes('completed')) return KIND.CLIENT_SIGNED;
    return KIND.ESIGNATURE_PENDING;
  }
  return KIND.MESSAGE_SENT;
}

function buildActionTitle(kind, item) {
  const title = (item.title || '').trim() || 'Activity';
  const user = (item.user || '').trim() || 'Staff';

  switch (kind) {
    case KIND.TASK_COMPLETED:
      return `Task completed — ${title}`;
    case KIND.MESSAGE_SENT:
      return 'Message sent to client';
    case KIND.FILE_UPLOADED:
      return `Document uploaded — ${title}`;
    case KIND.INVOICE_SENT: {
      const invMatch = title.match(/#\s*([^#]+?)\s*$/i) || title.match(/#\s*(\S+)/i);
      const num = invMatch ? invMatch[1].trim() : title.replace(/^invoice\s*/i, '').replace(/^#/i, '').trim();
      const summary = item.summary || '';
      const amt = summary.split('·')[0]?.trim() || '';
      return `Invoice #${num} sent${amt ? ` (${amt})` : ''}`;
    }
    case KIND.NOTE_ADDED:
      return `Note added by ${user}`;
    case KIND.WORKFLOW_ASSIGNED:
      return `Workflow assigned — ${title}`;
    case KIND.CLIENT_SIGNED:
      return 'Document signed by client';
    case KIND.PORTAL_LOGIN:
      return 'Client logged into portal';
    case KIND.ESIGNATURE_PENDING:
      return `E-signature — ${title}`;
    default:
      return title;
  }
}

function buildDescription(kind, item) {
  const summary = (item.summary || '').trim();
  switch (kind) {
    case KIND.MESSAGE_SENT:
      return summary || (item.title || '').trim() || '—';
    case KIND.NOTE_ADDED:
      return summary || '—';
    case KIND.CLIENT_SIGNED:
      return (item.title || '').trim() || summary || '—';
    case KIND.INVOICE_SENT:
      return summary.split('·').slice(1).join('·').trim() || '—';
    default:
      return summary || '—';
  }
}

function staffCaption(kind, item) {
  if (kind === KIND.NOTE_ADDED || kind === KIND.CLIENT_SIGNED || kind === KIND.PORTAL_LOGIN) {
    return null;
  }
  const u = (item.user || '').trim();
  return u || null;
}

function getTimelineVisual(kind) {
  switch (kind) {
    case KIND.TASK_COMPLETED:
    case KIND.WORKFLOW_ASSIGNED:
      return {
        Icon: kind === KIND.WORKFLOW_ASSIGNED ? AccountTreeOutlinedIcon : CheckCircleOutlineIcon,
        circleBg: 'warning.main',
        iconColor: 'warning.contrastText',
      };
    case KIND.MESSAGE_SENT:
      return {
        Icon: ChatBubbleOutlineIcon,
        circleBg: 'info.main',
        iconColor: 'info.contrastText',
      };
    case KIND.FILE_UPLOADED:
      return {
        Icon: InsertDriveFileOutlinedIcon,
        circleBg: 'secondary.main',
        iconColor: 'secondary.contrastText',
      };
    case KIND.INVOICE_SENT:
      return {
        Icon: ReceiptLongOutlinedIcon,
        circleBg: 'success.main',
        iconColor: 'success.contrastText',
      };
    case KIND.NOTE_ADDED:
      return {
        Icon: StickyNote2OutlinedIcon,
        circleBg: 'warning.light',
        iconColor: 'warning.dark',
      };
    case KIND.CLIENT_SIGNED:
      return {
        Icon: DrawOutlinedIcon,
        circleBg: 'success.main',
        iconColor: 'success.contrastText',
      };
    case KIND.PORTAL_LOGIN:
      return {
        Icon: LoginOutlinedIcon,
        circleBg: 'action.selected',
        iconColor: 'text.primary',
      };
    case KIND.ESIGNATURE_PENDING:
      return {
        Icon: DrawOutlinedIcon,
        circleBg: 'secondary.main',
        iconColor: 'secondary.contrastText',
      };
    default:
      return {
        Icon: TimelineIcon,
        circleBg: 'grey.500',
        iconColor: 'common.white',
      };
  }
}

function TimelineSkeleton() {
  return (
    <Stack spacing={0}>
      {Array.from({ length: 5 }).map((_, idx) => (
        <Box
          key={idx}
          sx={{
            display: 'flex',
            gap: 2,
            py: 2,
            position: 'relative',
            borderBottom: 1,
            borderColor: 'divider',
            '&:last-of-type': { borderBottom: 0 },
          }}
        >
          <Skeleton variant="circular" width={40} height={40} sx={{ flexShrink: 0 }} />
          <Box sx={{ flex: 1 }}>
            <Skeleton variant="rounded" height={72} sx={{ borderRadius: 1 }} />
          </Box>
        </Box>
      ))}
    </Stack>
  );
}

function TimelineEventRow({ item, isLast }) {
  const kind = classifyItem(item);
  const { Icon, circleBg, iconColor } = getTimelineVisual(kind);
  const actionTitle = buildActionTitle(kind, item);
  const description = buildDescription(kind, item);
  const rel = formatRelativeTime(item.timestamp);
  const staff = staffCaption(kind, item);

  return (
    <Box
      sx={{
        display: 'flex',
        gap: 2,
        position: 'relative',
        pb: isLast ? 0 : 3,
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: 40,
          flexShrink: 0,
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: '50%',
            bgcolor: circleBg,
            color: iconColor,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1,
            boxShadow: 1,
            border: 2,
            borderColor: 'background.paper',
          }}
        >
          <Icon sx={{ fontSize: 20 }} />
        </Box>
        {!isLast && (
          <Box
            sx={{
              position: 'absolute',
              left: '50%',
              transform: 'translateX(-50%)',
              top: 40,
              bottom: -24,
              width: '2px',
              bgcolor: 'divider',
              zIndex: 0,
            }}
          />
        )}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0, pt: 0.25 }}>
        <Card
          elevation={0}
          sx={{
            border: 1,
            borderColor: 'divider',
            borderRadius: 2,
            boxShadow: 1,
          }}
        >
          <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              {actionTitle}
            </Typography>
            {description && description !== '—' && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, whiteSpace: 'pre-wrap' }}>
                {description}
              </Typography>
            )}
            <Stack direction="row" flexWrap="wrap" alignItems="baseline" gap={1} sx={{ mt: 1.25 }}>
              <Typography variant="caption" color="text.secondary">
                {rel}
              </Typography>
              {staff && (
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                  · {staff}
                </Typography>
              )}
            </Stack>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}

function TimelineTab({ client }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(1);
  const [filterValue, setFilterValue] = useState('all');

  const typesParam = FILTER_OPTIONS.find((o) => o.value === filterValue)?.types ?? 'all';

  const fetchTimeline = useCallback(
    async (pageNum = 1, append = false) => {
      if (!client?.id) return;
      if (append) setLoadingMore(true);
      else setLoading(true);
      try {
        const params = { page: pageNum };
        if (typesParam !== 'all') params.types = typesParam;
        const res = await enhancedApi.getClientTimeline(client.id, params);
        const data = res.data || res;
        const newItems = data.items || [];
        setItems(append ? (prev) => [...prev, ...newItems] : newItems);
        setHasMore(!!data.has_more);
        setPage(pageNum);
      } catch {
        setItems([]);
        setHasMore(false);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [client?.id, typesParam]
  );

  useEffect(() => {
    fetchTimeline(1, false);
  }, [fetchTimeline]);

  const handleLoadMore = () => {
    fetchTimeline(page + 1, true);
  };

  return (
    <Box sx={{ width: 1, display: 'flex', flexDirection: 'column', gap: 3, pb: 2 }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        justifyContent="space-between"
        gap={2}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Timeline
        </Typography>
        <FormControl size="small" sx={{ minWidth: { xs: 1, sm: 260 } }}>
          <InputLabel id="timeline-filter-label">Filter</InputLabel>
          <Select
            labelId="timeline-filter-label"
            label="Filter"
            value={filterValue}
            onChange={(e) => setFilterValue(e.target.value)}
          >
            {FILTER_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      {loading && items.length === 0 ? (
        <TimelineSkeleton />
      ) : items.length === 0 ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            py: 8,
            px: 2,
            borderRadius: 1,
            border: 1,
            borderColor: 'divider',
            bgcolor: 'action.hover',
          }}
        >
          <TimelineIcon sx={{ fontSize: 56, color: 'action.disabled', mb: 2 }} />
          <Typography color="text.secondary">No activity yet</Typography>
        </Box>
      ) : (
        <>
          <Box>{items.map((item, idx) => (
            <TimelineEventRow key={item.id || idx} item={item} isLast={idx === items.length - 1} />
          ))}</Box>

          {hasMore && (
            <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1 }}>
              <Button variant="outlined" onClick={handleLoadMore} disabled={loadingMore}>
                {loadingMore ? 'Loading…' : 'Load more'}
              </Button>
            </Box>
          )}
        </>
      )}
    </Box>
  );
}

export default TimelineTab;
